import {
  Bill,
  BillsConsumptionsFormatted,
  BillsTableRow,
  ConsumptionBills,
  FiltersBills,
  GetBillsRequestBody,
  TotalEnvirormentState,
} from "../../../core/models/bills/bills.model";
import { Dataset } from "../../../core/models/consumptions/consumptions.model";
import { FormattedOwnerData } from "../../../core/models/ownerData/ownerData.model";
import { SuppliesTableRow } from "../../../core/models/supplies/supplies.model";
import theme from "../../../muiTheme";
import {
  addDayDate,
  formatDate,
  formatDateString,
  formatStringDate,
} from "../../dateUtils/formatDate.util";
import { formatNumber } from "../../formatters/formatters.util";

const initialFiltersBills: FiltersBills = {
  dateFrom: null,
  dateTo: null,
  emissionDateFrom: null,
  emissionDateTo: null,
  amountMin: null,
  amountMax: null,
  cups: [],
  cif: [],
  socialReason: [],
  environmentCode: "",
  billStateCode: [],
  invoiceNumberSerie: null,
  orderBy: {
    FechaFactura: "desc",
  },
};

const localStorageBillsFilters =
  process.env.REACT_APP_LOCALSTORAGE_BILLS_FILTERS!;
const removeLocalstorageBillsFilters = () =>
  localStorage.removeItem(localStorageBillsFilters);
const getLoacalstorageBillsFilters = () =>
  localStorage.getItem(localStorageBillsFilters);

const refreshLocalstorageBillsFilters = (billsFilters: object) =>
  localStorage.setItem(localStorageBillsFilters, JSON.stringify(billsFilters));

const setLocalstorageBillsFilters = (billsFilters: any) => {
  const filtersToStore = {
    ...billsFilters,
    dateFrom: billsFilters.dateFrom ? {$d: (billsFilters.dateFrom).$d} : null,
    dateTo: billsFilters.dateTo ? {$d: (billsFilters.dateTo).$d} : null,
    emissionDateFrom: billsFilters.emissionDateFrom ? {$d: (billsFilters.emissionDateFrom).$d} : null,
    emissionDateTo: billsFilters.emissionDateTo ? {$d: (billsFilters.emissionDateTo).$d} : null,
  };  
  localStorage.setItem(localStorageBillsFilters, JSON.stringify(filtersToStore));
};

const initialTotalEnvirormentState: TotalEnvirormentState = {
  totalCountGas: "",
  totalCountLight: "",
  totalCountPaid: "",
  totalCountPending: "",
  totalCountNoPaid: 0,
};

const formatBills = (bills: Bill[]): BillsTableRow[] => {
  return bills.map(
    ({
      IdContratoDocumento,
      IdFacturaVentaCabecera,
      FechaDesde,
      FechaHasta,
      FechaFactura,
      ImporteTotal,
      Estado_Fac,
      NombreCliente,
      Identidad,
      Entorno,
      IdContrato,
      CUPS,
    }) => {
      return {
        id: IdContratoDocumento,
        idExcel: IdFacturaVentaCabecera,
        service: Entorno,
        socialReason: NombreCliente,
        cif: Identidad,
        cups: CUPS,
        period: (FechaDesde && FechaHasta) 
        ? `${formatStringDate(FechaDesde)} - ${formatStringDate(FechaHasta)}` 
        : '- - -',
        amount: `${formatNumber(+ImporteTotal)} €`,
        state: Estado_Fac,
        contractId: IdContrato,
        emissionDate: formatStringDate(FechaFactura),
      };
    }
  );
};

const formatFiltersBills = ({
  dateFrom,
  dateTo,
  emissionDateFrom,
  emissionDateTo,
  cups,
  cif,
  environmentCode,
  amountMin,
  amountMax,
  billStateCode,
  socialReason,
  invoiceNumberSerie,
}: FiltersBills) => ({
  ...(dateFrom &&
    dateTo && {
      dateFromTo: [`${formatDate(dateFrom.$d)} - ${formatDate(dateTo.$d)}`],
    }),
  ...(emissionDateFrom &&
    emissionDateTo && {
      emissionDateFromTo: [
        `${formatDate(emissionDateFrom.$d)} - ${formatDate(emissionDateTo.$d)}`,
      ],
    }),
  cups,
  cif,
  ...((amountMin || amountMax) && {
    importMinMax: [
      `${amountMin ? formatNumber(amountMin) : 0}€ - ${
        amountMax ? formatNumber(amountMax) : "X"
      }€`,
    ],
  }),
  socialReason,
  environmentCode: environmentCode.length ? [environmentCode] : [],
  billStateCode: billStateCode ? [billStateCode] : [],
  invoiceNumberSerie: invoiceNumberSerie ? [invoiceNumberSerie] : [],
});

const formatBillsConsumptions = (
  billsConsumptions: ConsumptionBills[]
): BillsConsumptionsFormatted[] =>
  billsConsumptions.map(
    ({ FechaFactura, Importe, FechaInicioFactura, FechaFinalFactura }) => ({
      invoiceDate: formatStringDate(FechaFactura),
      amount: Importe,
      dateFrom: formatStringDate(FechaInicioFactura),
      dateTo: formatStringDate(FechaFinalFactura),
    })
  );

const getDatesFromTo = ({
  dateTo,
  dateFrom,
}: FiltersBills): {
  dateTo: string | Date | null;
  dateFrom: string | Date | null;
} => {
  const isNotDateToFrom = !dateTo || !dateFrom;
  if (isNotDateToFrom) {
    return {
      dateFrom: null,
      dateTo: null,
    };
  }
  return { dateTo: dateTo?.$d as Date, dateFrom: dateFrom?.$d as Date };
};

const getEmissionDatesFromTo = ({
  emissionDateFrom,
  emissionDateTo,
}: FiltersBills): {
  emissionDateTo: string | Date | null;
  emissionDateFrom: string | Date | null;
} => {
  const isNotDateToFrom = !emissionDateTo || !emissionDateFrom;
  if (isNotDateToFrom) {
    return {
      emissionDateFrom: null,
      emissionDateTo: null,
    };
  }

  return {
    emissionDateTo: emissionDateTo?.$d as Date,
    emissionDateFrom: emissionDateFrom?.$d as Date,
  };
};

const getDatasets = (
  billsConsumptionsFormatted: BillsConsumptionsFormatted[]
): Dataset[] => [
  {
    data: billsConsumptionsFormatted.map(({ amount }) => amount),
    backgroundColor: theme.palette.consumptions.period4,
    borderWidth: [
      {
        right: 0,
        left: 0,
        top: 0,
        bottom: 0,
      },
    ],
    borderColor: theme.palette.grey[700],
    borderSkipped: false,
  },
];

const getFiltersValues = (
  conditionValue: string | undefined,
  filterValues: string[],
  ownerDataValues: string[]
) => {
  if (conditionValue) {
    return [conditionValue];
  }

  return filterValues.length ? filterValues : [];
};

const getBillRequestBody = (
  { isAdministrator, idClient, idAdministrator, cups }: FormattedOwnerData,
  filtersBills: FiltersBills,
  page: number,
  rowsPerPage: number,
  selectedSupply: SuppliesTableRow | null,
  {
    dateTo,
    dateFrom,
  }: { dateTo: string | Date | null; dateFrom: string | Date | null },
  {
    emissionDateTo,
    emissionDateFrom,
  }: {
    emissionDateTo: string | Date | null;
    emissionDateFrom: string | Date | null;
  }
): GetBillsRequestBody => ({
  userid: isAdministrator ? idAdministrator : idClient,
  isAdmin: isAdministrator,
  isLastBillComponent: false,
  cups: getFiltersValues(
    selectedSupply?.cups.cupCode,
    filtersBills.cups,
    cups.map(({ codeCups }) => codeCups)
  ),
  cif: getFiltersValues(
    selectedSupply?.cif,
    filtersBills.cif,
    cups.map(({ cif }) => cif)
  ),
  dateFrom: dateFrom
    ? formatDateString(addDayDate(dateFrom as Date)).replace(",", "")
    : null,
  dateTo: dateTo ? formatDateString(addDayDate(dateTo as Date)).replace(",", "") : null,
  emissionDateFrom: emissionDateFrom
    ? formatDateString(addDayDate(emissionDateFrom as Date)).replace(",", "")
    : null,
  emissionDateTo: emissionDateTo
    ? formatDateString(addDayDate(emissionDateTo as Date)).replace(",", "")
    : null,
  environmentCode: filtersBills.environmentCode,
  billStateCode: filtersBills.billStateCode.length > 0 ? filtersBills.billStateCode : [],
  pageSize: rowsPerPage.toString(),
  pageIndex: page.toString(),
  // sortByDate: true,
  // sortByDateAsc: orderByAscending,
  contractId: selectedSupply?.cups.contractId
    ? +selectedSupply?.cups.contractId
    : undefined,
  invoiceNumberSerie: filtersBills.invoiceNumberSerie,
  minBillImport: filtersBills.amountMin,
  maxBillImport: filtersBills.amountMax,
  businessName: getFiltersValues(
    selectedSupply?.socialReason,
    filtersBills.socialReason,
    cups.map(({ socialReason }) => socialReason)
  ),
  orderBy: filtersBills.orderBy,
});

const getEnvirormentStateLabel = (key: string, value: string) => {
  if (key === "environmentCode") {
    return value === "E1" ? "Luz" : "Gas";
  }

  return value === "Pendiente"
    ? "Pendientes"
    : value === "Pagado"
    ? "Pagadas"
    : "No pagadas";
};

const getBillsIdDocumentsListRequestBody = (
  ownerData: FormattedOwnerData | null,
  {
    cups: filtersCups,
    cif: filtersCif,
    socialReason: filtersSocialReason,
    dateFrom: filtersDateFrom,
    dateTo: filtersDateTo,
    emissionDateFrom: filtersEmissionDateFrom,
    emissionDateTo: filtersEmissionDateTo,
    invoiceNumberSerie,
    amountMin,
    amountMax,
  }: FiltersBills,
  selectedSupply: SuppliesTableRow | null
): GetBillsRequestBody => {
  const {
    isAdministrator,
    idClient,
    idAdministrator,
    cups: ownerDataCups,
  } = ownerData as FormattedOwnerData;
  const isFiltersCifCupsSocialReason =
    !!filtersSocialReason.length || !!filtersCups.length || !!filtersCif.length;
  const cups = selectedSupply
    ? [selectedSupply.cups.cupCode]
    : isFiltersCifCupsSocialReason
    ? filtersCups
    : [...new Set(ownerDataCups.map(({ codeCups }) => codeCups))];
  const cif = selectedSupply
    ? [selectedSupply.cif]
    : isFiltersCifCupsSocialReason
    ? filtersCif
    : [...new Set(ownerDataCups.map(({ cif }) => cif))];
  const socialReason = selectedSupply
    ? [selectedSupply.socialReason]
    : isFiltersCifCupsSocialReason
    ? filtersSocialReason
    : [...new Set(ownerDataCups.map(({ socialReason }) => socialReason))];
  const dateFrom = filtersDateFrom?.$d ? addDayDate(filtersDateFrom.$d) : null;
  const dateTo = filtersDateTo?.$d ? addDayDate(filtersDateTo.$d) : null;
  const emissionDateFrom = filtersEmissionDateFrom?.$d ? addDayDate(filtersEmissionDateFrom.$d) : null;
  const emissionDateTo = filtersEmissionDateTo?.$d ? addDayDate(filtersEmissionDateTo.$d) : null;

  return {
    userid: isAdministrator ? idAdministrator : idClient,
    isAdmin: isAdministrator,
    cups: cups.length
      ? cups
      : ownerDataCups
          .filter(({ cif: codeCupsCif }) => cif.includes(codeCupsCif))
          .filter(({ socialReason: ownerSocialReason }) =>
            socialReason.includes(ownerSocialReason)
          )
          .map(({ codeCups }) => codeCups),
    cif: cif.length
      ? cif
      : ownerDataCups
          .filter(({ codeCups }) => cups.includes(codeCups))
          .filter(({ socialReason: ownerSocialReason }) =>
            socialReason.includes(ownerSocialReason)
          )
          .map(({ cif }) => cif),
    businessName: socialReason.length
      ? socialReason
      : ownerDataCups
          .filter(({ cif: codeCupsCif }) => cif.includes(codeCupsCif))
          .filter(({ codeCups }) => cups.includes(codeCups))
          .map(({ socialReason }) => socialReason),
    contractId: selectedSupply ? Number(selectedSupply.cups.contractId) : undefined,
    dateFrom: dateFrom ? formatDateString(dateFrom).split(" ")[0] : null,
    dateTo: dateTo ? formatDateString(dateTo).split(" ")[0] : null,
    emissionDateFrom: emissionDateFrom
      ? formatDateString(emissionDateFrom).split(" ")[0]
      : null,
    emissionDateTo: emissionDateTo
      ? formatDateString(emissionDateTo).split(" ")[0]
      : null,
    minBillImport: amountMin,
    maxBillImport: amountMax,
    invoiceNumberSerie,
  };
};

const getDownloadBillsListRequestBody = (
  requestId: string,
  billsIdListLength: number,
  indexNumber: number,
  fileType: string,
  list: string[]
) => ({
  requestId,
  isLastRequest: billsIdListLength - 1 === indexNumber ? true : false,
  isFirstRequest: !indexNumber ? true : false,
  fileType,
  billIdList: list,
});

const chunkArray = (billsList: string[], size: number) =>
  Array.from(
    { length: Math.ceil(billsList.length / size) },
    (billId, indexNumber) =>
      billsList.slice(indexNumber * size, indexNumber * size + size)
  );

const downloadBillsDocument = (
  url: string,
  link: HTMLAnchorElement,
  name?: string
) => {
  link.setAttribute("href", url);
  name && link.setAttribute("download", name);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export {
  initialFiltersBills,
  initialTotalEnvirormentState,
  formatBills,
  formatFiltersBills,
  formatBillsConsumptions,
  getDatesFromTo,
  getDatasets,
  getFiltersValues,
  getBillRequestBody,
  getEnvirormentStateLabel,
  getBillsIdDocumentsListRequestBody,
  getDownloadBillsListRequestBody,
  chunkArray,
  downloadBillsDocument,
  removeLocalstorageBillsFilters,
  setLocalstorageBillsFilters,
  refreshLocalstorageBillsFilters,
  getLoacalstorageBillsFilters,
  getEmissionDatesFromTo,
};
